import React, {useEffect, useState} from "react";
import CurrencyInput from "react-currency-input-field";
import {FormikCurrencyInputProps} from "@types/FormikCurrencyInputProps";

/**
 * Custom Formik Currency Input component.
 * Integrates with Formik forms and uses `react-currency-input-field` for handling formatted inputs.
 *
 * @param {FormikCurrencyInputProps} props - Props passed from Formik.
 * @returns {JSX.Element} - Rendered CurrencyInput component.
 */
const FormikCurrencyInput: React.FC<FormikCurrencyInputProps> = ({
                                                                     field,
                                                                     form,
                                                                     placeholder,
                                                                     className,
                                                                     onFocus,
                                                                     ...props
                                                                 }: FormikCurrencyInputProps): JSX.Element => {
    const {name} = field;
    const {setFieldValue, setFieldTouched, values} = form;

    const [inputValue, setInputValue] = useState<string | undefined>(
        values[name]?.toString() ?? ""
    );

    useEffect(() => {
        // Update input value if Formik value changes externally
        const formikValue = values[name]?.toString() ?? "";
        if (formikValue !== inputValue) {
            setInputValue(formikValue);
        }
    }, [values[name]]);

    const handleValueChange = (value: string | undefined, _name: string | undefined) => {
        setInputValue(value ?? "");

        // Convert string to number, replacing comma with dot for decimal
        const numericValue = value
            ? parseFloat(value.replace(/\./g, "").replace(",", "."))
            : "";

        setFieldValue(name, numericValue);
    };

    const handleBlurEvent = () => {
        setFieldTouched(name, true);
    };

  const { ["data-clinic-name"]: dataClinicName } = props;

  return (
        <CurrencyInput
            id={name}
            name={name}
            value={inputValue}
            className={className}
            allowNegativeValue={false}
            disableAbbreviations
            decimalSeparator=","
            groupSeparator="."
            decimalScale={2}
            placeholder={placeholder}
            onValueChange={handleValueChange}
            onBlur={handleBlurEvent}
            onFocus={onFocus}
            data-clinic-name={dataClinicName}
        />
    );
};

export default FormikCurrencyInput;
